import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Pencil, Plus, RotateCcw, Settings2, ShoppingCart, Trash2, Trophy } from "lucide-react";
import { botDelete, botGet, botPut, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChannelSelect, RoleSelect } from "@/components/pickers";
import {
  ChannelMulti, Empty, FeatureGate, Field, Loading, MessageMini, NumberInput, PageHeader, Panel, PillTabs, RoleMulti, SaveBar, SimpleSelect,
  TextInput, ToggleRow, cleanMessage, dangerBtn, outlineBtn, primaryBtn, useFeature,
} from "@/components/kit";

const MEDAL = ["🥇", "🥈", "🥉"];

function RankRow({ u, children, onEdit }) {
  return (
    <div className="flex items-center gap-3 bg-[#131620] px-4 py-3 transition-colors hover:bg-[#1a1e2b]">
      <span className="w-8 text-center text-sm font-bold text-[#949BA4]">{MEDAL[u.rank - 1] ?? `#${u.rank}`}</span>
      {u.avatarUrl ? <img src={u.avatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-[#232838]" />}
      <div className="min-w-0 flex-1"><p className="font-semibold text-white">{u.username}</p>{children}</div>
      <Button size="icon" variant="ghost" onClick={onEdit} className="text-[#949BA4] hover:bg-white/5 hover:text-white"><Pencil className="h-4 w-4" /></Button>
    </div>
  );
}

function EditValue({ open, title, label, value, suffix, onClose, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value, open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
        <DialogHeader><DialogTitle className="text-white">{title}</DialogTitle><DialogDescription className="text-[#949BA4]">Il cambiamento è immediato.</DialogDescription></DialogHeader>
        <Field label={label}><NumberInput value={v} max={1e12} className="w-40" suffix={suffix} onChange={setV} /></Field>
        <DialogFooter><Button onClick={() => onSave(v)} className={primaryBtn}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Livelli ───────────────────────────
export default function Levels() {
  const [tab, setTab] = useState("board");
  return (
    <div className="space-y-6">
      <PageHeader icon={Trophy} title="Livelli" subtitle="Esperienza per messaggi e tempo in vocale, classifica e ruoli premio." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "board", label: "Classifica", icon: Trophy }, { id: "settings", label: "Impostazioni", icon: Settings2 }]} />
      {tab === "board" ? <XpBoard /> : <LevelSettings />}
    </div>
  );
}

function XpBoard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reset, setReset] = useState(false);
  const load = useCallback(() => botGet("levels/leaderboard", { limit: 50 }).then(setData).catch((e) => { toast.error(apiError(e)); setData({ users: [] }); }), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      {data.users.length === 0 ? <Empty icon={Trophy} title="Classifica vuota">Attiva i livelli: i membri guadagnano XP scrivendo e stando in vocale.</Empty> : (
        <div className="divide-y divide-[#232838] overflow-hidden rounded-2xl border border-[#232838]">
          {data.users.map((u) => (
            <RankRow key={u.userId} u={u} onEdit={() => setEditing(u)}>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#5865F2]/20 px-2 py-0.5 text-[11px] font-bold text-[#a5b0ff]">Livello {u.level}</span>
                <div className="h-1.5 w-40 overflow-hidden rounded-full bg-[#232838]"><div className="h-full rounded-full bg-gradient-to-r from-[#5865F2] to-[#00F0FF]" style={{ width: `${Math.round((u.current / u.needed) * 100)}%` }} /></div>
                <span className="text-[11px] text-[#949BA4]">{u.xp.toLocaleString("it-IT")} XP · {u.messages} messaggi · {u.voiceMinutes ?? 0} min in vocale</span>
              </div>
            </RankRow>
          ))}
        </div>
      )}
      <Button variant="outline" onClick={() => setReset(true)} className="border-[#F23F43]/40 bg-transparent text-[#ff7a7d] hover:bg-[#F23F43]/10"><RotateCcw className="mr-2 h-4 w-4" />Azzera classifica</Button>
      <EditValue open={!!editing} title={`XP di ${editing?.username}`} label="Esperienza totale" suffix="XP" value={editing?.xp ?? 0} onClose={() => setEditing(null)}
        onSave={async (xp) => { try { await botPut(`levels/users/${editing.userId}`, { xp }); toast.success("XP aggiornati"); setEditing(null); load(); } catch (e) { toast.error(apiError(e)); } }} />
      <Dialog open={reset} onOpenChange={setReset}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Azzerare la classifica?</DialogTitle><DialogDescription className="text-[#949BA4]">Tutti tornano a 0 XP. Non si può annullare.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setReset(false)} className={outlineBtn}>Annulla</Button>
            <Button className={dangerBtn} onClick={async () => { try { await botDelete("levels"); toast.success("Classifica azzerata"); setReset(false); load(); } catch (e) { toast.error(apiError(e)); } }}>Azzera</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LevelSettings() {
  const f = useFeature("levels");
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-4">
          <ToggleRow title={v.enabled ? "Livelli attivi" : "Livelli disattivati"} desc="I membri guadagnano esperienza e salgono di livello." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} testId="levels-toggle" />
          <Panel title="Come si guadagna esperienza" icon={Trophy}>
            <div className="space-y-3 text-sm text-[#DBDEE1]">
              <div className="flex flex-wrap items-center gap-2">Per messaggio da <NumberInput value={v.xpMin} max={1000} className="w-20" onChange={(xpMin) => f.change({ xpMin })} /> a <NumberInput value={v.xpMax} max={1000} className="w-20" suffix="XP" onChange={(xpMax) => f.change({ xpMax })} /></div>
              <div className="flex flex-wrap items-center gap-2">Al massimo una volta ogni <NumberInput value={v.cooldownSeconds} max={3600} className="w-20" suffix="secondi" onChange={(cooldownSeconds) => f.change({ cooldownSeconds })} /></div>
              <div className="flex flex-wrap items-center gap-2">In vocale <NumberInput value={v.voiceXpPerMinute} max={1000} className="w-20" suffix="XP al minuto (0 = niente)" onChange={(voiceXpPerMinute) => f.change({ voiceXpPerMinute })} /></div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Field label="Canali senza XP"><ChannelMulti value={v.noXpChannelIds} onChange={(noXpChannelIds) => f.change({ noXpChannelIds })} /></Field>
              <Field label="Ruoli senza XP"><RoleMulti value={v.noXpRoleIds} onChange={(noXpRoleIds) => f.change({ noXpRoleIds })} /></Field>
            </div>
          </Panel>
          <Panel title="Ruoli premio" desc="Raggiunto il livello, il bot dà il ruolo in automatico."
            right={<Button size="sm" className={primaryBtn} onClick={() => f.change({ rewards: [...v.rewards, { level: (v.rewards.at(-1)?.level ?? 0) + 5, roleId: null }] })}><Plus className="mr-1 h-4 w-4" />Premio</Button>}>
            <div className="space-y-2">
              {v.rewards.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#232838] bg-[#131620] p-3 text-sm text-[#DBDEE1]">
                  Livello <NumberInput value={r.level} min={1} max={1000} className="w-20" onChange={(level) => f.change({ rewards: v.rewards.map((x, j) => (j === i ? { ...x, level } : x)) })} />
                  <div className="min-w-[180px] flex-1"><RoleSelect value={r.roleId} onChange={(roleId) => f.change({ rewards: v.rewards.map((x, j) => (j === i ? { ...x, roleId } : x)) })} /></div>
                  <Button size="icon" variant="ghost" onClick={() => f.change({ rewards: v.rewards.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              {v.rewards.length === 0 && <p className="text-sm text-[#949BA4]">Nessun ruolo premio.</p>}
              <ToggleRow title="Accumula i ruoli premio" desc="Disattivato: tiene solo il ruolo del livello più alto." checked={v.stackRewards} onChange={(stackRewards) => f.change({ stackRewards })} />
            </div>
          </Panel>
          <Panel title="Messaggio di livello superiore">
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Dove annunciarlo"><SimpleSelect value={v.announceMode} onChange={(announceMode) => f.change({ announceMode })}
                  options={[{ value: "current", label: "Nel canale dove ha scritto" }, { value: "channel", label: "In un canale fisso" }, { value: "dm", label: "In privato" }, { value: "none", label: "Non annunciare" }]} /></Field>
                {v.announceMode === "channel" && <Field label="Canale"><ChannelSelect value={v.announceChannelId} onChange={(announceChannelId) => f.change({ announceChannelId })} /></Field>}
              </div>
              {v.announceMode !== "none" && <MessageMini value={v.levelUpMessage} onChange={(levelUpMessage) => f.change({ levelUpMessage })} hint="Segnaposto extra: {level} nuovo livello, {xp} esperienza totale." />}
            </div>
          </Panel>
          <SaveBar feature={f} prepare={(x) => ({ ...x, levelUpMessage: cleanMessage(x.levelUpMessage), rewards: x.rewards.filter((r) => r.roleId), multipliers: x.multipliers.filter((m) => m.roleId) })} />
        </div>
      )}
    </FeatureGate>
  );
}

// ─────────────────────────── Economia ───────────────────────────
export function Economy() {
  const [tab, setTab] = useState("board");
  const f = useFeature("economy");
  return (
    <div className="space-y-6">
      <PageHeader icon={Coins} title="Economia" subtitle="Monete virtuali: ricompensa giornaliera, lavori, pagamenti e negozio di ruoli." />
      <PillTabs active={tab} onChange={setTab} tabs={[{ id: "board", label: "Classifica", icon: Coins }, { id: "shop", label: "Negozio", icon: ShoppingCart }, { id: "settings", label: "Impostazioni", icon: Settings2 }]} />
      <FeatureGate feature={f}>
        {(v) => (
          <>
            {tab === "board" && <Richest currency={v} />}
            {tab === "shop" && <Shop f={f} v={v} />}
            {tab === "settings" && <EconomySettings f={f} v={v} />}
          </>
        )}
      </FeatureGate>
    </div>
  );
}

const money = (n, v) => `${v.currencyEmoji ? v.currencyEmoji + " " : ""}${Number(n).toLocaleString("it-IT")} ${v.currencyName}`;

function Richest({ currency }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => botGet("economy/leaderboard", { limit: 50 }).then(setData).catch(() => setData({ users: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      {data.users.length === 0 ? <Empty icon={Coins} title="Nessuno ha ancora monete">I membri usano /daily e /work per guadagnare.</Empty> : (
        <div className="divide-y divide-[#232838] overflow-hidden rounded-2xl border border-[#232838]">
          {data.users.map((u) => (
            <RankRow key={u.userId} u={u} onEdit={() => setEditing(u)}>
              <p className="text-xs text-[#949BA4]"><span className="font-semibold text-[#FEE75C]">{money(u.balance, currency)}</span> · guadagnate {Number(u.earned ?? 0).toLocaleString("it-IT")} · spese {Number(u.spent ?? 0).toLocaleString("it-IT")}</p>
            </RankRow>
          ))}
        </div>
      )}
      <EditValue open={!!editing} title={`Saldo di ${editing?.username}`} label="Saldo" suffix={currency.currencyName} value={editing?.balance ?? 0} onClose={() => setEditing(null)}
        onSave={async (balance) => { try { await botPut(`economy/users/${editing.userId}`, { balance }); toast.success("Saldo aggiornato"); setEditing(null); load(); } catch (e) { toast.error(apiError(e)); } }} />
    </div>
  );
}

function Shop({ f, v }) {
  const setItem = (i, patch) => f.change({ shop: v.shop.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  return (
    <div className="space-y-4">
      {v.shop.length === 0 && <Empty icon={ShoppingCart} title="Negozio vuoto">Aggiungi articoli: i membri li comprano con /buy.</Empty>}
      <div className="grid gap-4 md:grid-cols-2">
        {v.shop.map((item, i) => (
          <Panel key={item.id} title={item.name || "Nuovo articolo"} icon={ShoppingCart}
            right={<Button size="icon" variant="ghost" onClick={() => f.change({ shop: v.shop.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>}>
            <div className="space-y-3">
              <Field label="Nome"><TextInput value={item.name} onChange={(name) => setItem(i, { name })} maxLength={80} /></Field>
              <Field label="Descrizione"><TextInput value={item.description} onChange={(description) => setItem(i, { description })} maxLength={200} /></Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Prezzo"><NumberInput value={item.price} className="w-32" suffix={v.currencyName} onChange={(price) => setItem(i, { price })} /></Field>
                <Field label="Ruolo che si ottiene"><RoleSelect value={item.roleId} onChange={(roleId) => setItem(i, { roleId })} /></Field>
              </div>
            </div>
          </Panel>
        ))}
      </div>
      <Button onClick={() => f.change({ shop: [...v.shop, { id: `item-${Date.now().toString(36)}`, name: "", description: "", price: 1000, roleId: null }] })} className={primaryBtn}><Plus className="mr-2 h-4 w-4" />Aggiungi articolo</Button>
      <SaveBar feature={f} prepare={(x) => ({ ...x, shop: x.shop.filter((s) => s.name.trim()).map((s) => ({ ...s, description: s.description || undefined })) })} />
    </div>
  );
}

function EconomySettings({ f, v }) {
  return (
    <div className="space-y-4">
      <ToggleRow title={v.enabled ? "Economia attiva" : "Economia disattivata"} desc="Abilita /daily, /work, /pay, /shop, /buy." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
      <Panel title="Valuta" icon={Coins} desc={`Esempio: ${money(1250, v)}`}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Nome"><TextInput value={v.currencyName} onChange={(currencyName) => f.change({ currencyName })} maxLength={30} /></Field>
          <Field label="Emoji"><TextInput value={v.currencyEmoji} onChange={(currencyEmoji) => f.change({ currencyEmoji })} maxLength={60} /></Field>
          <Field label="Saldo iniziale"><NumberInput value={v.startingBalance} className="w-32" onChange={(startingBalance) => f.change({ startingBalance })} /></Field>
        </div>
      </Panel>
      <Panel title="Guadagni">
        <div className="space-y-3 text-sm text-[#DBDEE1]">
          <div className="flex flex-wrap items-center gap-2">/daily dà <NumberInput value={v.dailyAmount} className="w-28" suffix="ogni 24 ore" onChange={(dailyAmount) => f.change({ dailyAmount })} /></div>
          <div className="flex flex-wrap items-center gap-2">/work dà da <NumberInput value={v.workMin} className="w-24" onChange={(workMin) => f.change({ workMin })} /> a <NumberInput value={v.workMax} className="w-24" onChange={(workMax) => f.change({ workMax })} /> ogni <NumberInput value={v.workCooldownMinutes} min={1} max={10080} className="w-20" suffix="minuti" onChange={(workCooldownMinutes) => f.change({ workCooldownMinutes })} /></div>
          <div className="flex flex-wrap items-center gap-2">Per ogni messaggio <NumberInput value={v.messageReward} max={1e6} className="w-24" suffix="(max 1 al minuto, 0 = niente)" onChange={(messageReward) => f.change({ messageReward })} /></div>
        </div>
      </Panel>
      <SaveBar feature={f} />
    </div>
  );
}
